import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ListChecks,
  RefreshCw,
  Search,
  Plus,
  Edit2,
  Trash2,
  Download,
  Upload,
  FileSpreadsheet,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Leaf,
} from 'lucide-react';
import { Button, Card, LoadingState, Modal, Input, SearchableSelect } from '@/components/common';
import {
  getListaInvestitii,
  actualizeazaObiectiv,
  adaugaObiectiv,
  stergeObiectiv,
  incarcaFisierLista,
  getFisiereLista,
  activeazaFisierLista,
  getProiecteLegabile,
  descarcaFisierListaInvestitii,
  numeDescarcare,
  conflictObiectiv,
  mesajEroareLista,
  mesajEroareScriere,
  miiLei,
  miiLeiEditabil,
  parseazaMiiLei,
  surseleNuSeInchid,
  sumaSurselor,
  esteCheltuialaVerde,
  textCautare,
  dataLunga,
  dataOra,
  marimeFisier,
} from '@/services/listaInvestitii.service';
import type {
  DateObiectivInvestitie,
  ObiectivInvestitie,
  RaspunsFisiereListaInvestitii,
  RaspunsListaInvestitii,
  SectiuneInvestitie,
  SursaFinantare,
} from '@/types';

// ============================================================================
// Administrare — „Lista de investiții".
//
// Două lucruri SEPARATE, deliberat (decizia utilizatorului, 18.09.2026):
//   1. fișierul Excel servit la „Descarcă Excel" — se înlocuiește prin încărcare,
//      dar NU se reimportă: cifrele din tabel nu se schimbă când se urcă un fișier;
//   2. obiectivele (sumele, textele, rândurile) — se schimbă DOAR prin editare aici.
// Pagina le ține în carduri diferite și o spune explicit la încărcare, ca nimeni
// să nu creadă că a „actualizat lista" urcând un Excel.
//
// Sumele se introduc în MII LEI (unitatea documentului oficial) și pleacă spre
// backend în LEI. Conversia și regula de citire a numerelor sunt în serviciu
// (`parseazaMiiLei`), ca să existe una singură.
// ============================================================================

const LIMITA_FISIER = 30 * 1024 * 1024; // aceeași limită ca pe server

// Etichetele exact cum sunt în bază (inclusiv „cheltuiala" fără ă, din Excel).
// O a treia grafie ar sparge gruparea și ar scăpa de `esteCheltuialaVerde`.
const ETICHETA_VERDE = 'cheltuială verde/favorabilă';
const ETICHETA_NEUTRA = 'cheltuiala neutră';

// Ordinea surselor ca în nomenclatorul backendului; folosită dacă răspunsul nu
// aduce nomenclatorul (nu ar trebui, dar formularul nu are voie să rămână fără câmpuri).
const CODURI_SURSE: SursaFinantare[] = [
  'buget_judet', 'excedent', 'credite_interne', 'credite_externe',
  'donatii', 'hg', 'alte_surse', 'buget_stat',
];

const CAPITOL_NOU = '__nou';

export function ListaInvestitiiAdminPage() {
  const [date, setDate] = useState<RaspunsListaInvestitii | null>(null);
  const [seIncarca, setSeIncarca] = useState(true);
  const [eroare, setEroare] = useState<string | null>(null);

  const [fisiere, setFisiere] = useState<RaspunsFisiereListaInvestitii | null>(null);
  const [eroareFisiere, setEroareFisiere] = useState<string | null>(null);
  const [proiecte, setProiecte] = useState<{ id: number; nume: string }[]>([]);

  const [cauta, setCauta] = useState('');
  const [capitol, setCapitol] = useState('');
  const [sectiune, setSectiune] = useState<'' | SectiuneInvestitie>('');

  const [formDeschis, setFormDeschis] = useState(false);
  const [deEditat, setDeEditat] = useState<ObiectivInvestitie | null>(null);

  const [deSters, setDeSters] = useState<ObiectivInvestitie | null>(null);
  const [seSterge, setSeSterge] = useState(false);

  const inputFisier = useRef<HTMLInputElement>(null);
  const [fisierAles, setFisierAles] = useState<File | null>(null);
  const [seIncarcaFisier, setSeIncarcaFisier] = useState(false);
  const [seActiveaza, setSeActiveaza] = useState<number | 'initial' | null>(null);
  const [seDescarca, setSeDescarca] = useState(false);

  // Întoarce și răspunsul, nu doar îl pune în stare: formularul are nevoie de
  // rândul proaspăt după un conflict, iar starea React nu e gata pe loc.
  const incarcaLista = useCallback(async (): Promise<RaspunsListaInvestitii | null> => {
    setEroare(null);
    try {
      const r = await getListaInvestitii();
      setDate(r);
      return r;
    } catch (err) {
      console.error('Lista de investiții (admin):', err);
      setEroare(mesajEroareLista(err));
      return null;
    }
  }, []);

  const incarcaFisiere = useCallback(async () => {
    setEroareFisiere(null);
    try {
      setFisiere(await getFisiereLista());
    } catch (err) {
      console.error('Istoric fișiere listă investiții:', err);
      setEroareFisiere(mesajEroareScriere(err, 'Istoricul fișierelor nu a putut fi încărcat.'));
    }
  }, []);

  const incarcaTot = useCallback(async () => {
    setSeIncarca(true);
    await Promise.all([incarcaLista(), incarcaFisiere()]);
    setSeIncarca(false);
  }, [incarcaLista, incarcaFisiere]);

  useEffect(() => {
    incarcaTot();
    // Proiectele trebuie doar formularului; dacă nu vin, legătura rămâne cum e
    // și restul paginii funcționează.
    getProiecteLegabile()
      .then(setProiecte)
      .catch((err) => console.error('Proiecte pentru legătură:', err));
  }, [incarcaTot]);

  const obiective = date?.obiective ?? [];
  const versiune = date?.versiune ?? null;
  const fisier = date?.fisier ?? null;
  const an = versiune?.an ?? new Date().getFullYear();
  const numeCapitol = (cod: string) => date?.capitole?.[cod] ?? '';

  const indexCautare = useMemo(() => {
    const m = new Map<number, string>();
    obiective.forEach((o) => m.set(o.id, textCautare(o)));
    return m;
  }, [obiective]);

  const filtrate = useMemo(() => {
    const q = cauta.trim().toLowerCase();
    return obiective.filter((o) => {
      if (capitol && o.capitol !== capitol) return false;
      if (sectiune && o.sectiune !== sectiune) return false;
      if (q && !(indexCautare.get(o.id) ?? '').includes(q)) return false;
      return true;
    });
  }, [obiective, cauta, capitol, sectiune, indexCautare]);

  // Grupare pe capitol în ordinea în care vin rândurile (backendul le ordonează
  // pe capitol, secțiune, rând din Excel — cele adăugate manual la finalul secțiunii).
  const grupuri = useMemo(() => {
    const m = new Map<string, ObiectivInvestitie[]>();
    filtrate.forEach((o) => {
      if (!m.has(o.capitol)) m.set(o.capitol, []);
      m.get(o.capitol)!.push(o);
    });
    return Array.from(m.entries());
  }, [filtrate]);

  const capitoleDisponibile = useMemo(() => {
    const vazute: string[] = [];
    obiective.forEach((o) => {
      if (!vazute.includes(o.capitol)) vazute.push(o.capitol);
    });
    return vazute;
  }, [obiective]);

  const totalFiltrat = useMemo(() => filtrate.reduce((t, o) => t + o.alocatAn, 0), [filtrate]);

  // ---------------------------------------------------------------- fișierul

  const descarca = async () => {
    if (!fisier) return;
    setSeDescarca(true);
    try {
      await descarcaFisierListaInvestitii(numeDescarcare(fisier));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Descărcarea nu a reușit.');
    } finally {
      setSeDescarca(false);
    }
  };

  // Validarea din client e doar pentru un răspuns rapid; serverul verifică din
  // nou (și conținutul ZIP, nu doar extensia).
  const alegeFisier = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    // Golit imediat, ca aceeași alegere repetată (după o eroare) să declanșeze din nou `onChange`.
    e.target.value = '';
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) {
      toast.error('Se acceptă doar fișiere Excel .xlsx.');
      return;
    }
    if (f.size === 0) {
      toast.error('Fișierul ales este gol.');
      return;
    }
    if (f.size > LIMITA_FISIER) {
      toast.error(`Fișierul are ${marimeFisier(f.size)}; limita este de 30 MB.`);
      return;
    }
    setFisierAles(f);
  };

  const confirmaIncarcarea = async () => {
    if (!fisierAles) return;
    setSeIncarcaFisier(true);
    try {
      await incarcaFisierLista(fisierAles);
      toast.success('Fișierul a fost încărcat și se servește de acum la „Descarcă Excel”.');
      setFisierAles(null);
      await Promise.all([incarcaLista(), incarcaFisiere()]);
    } catch (err) {
      console.error('Încărcare fișier listă investiții:', err);
      toast.error(mesajEroareScriere(err, 'Încărcarea fișierului nu a reușit.'));
    } finally {
      setSeIncarcaFisier(false);
    }
  };

  const activeaza = async (id: number | 'initial') => {
    setSeActiveaza(id);
    try {
      await activeazaFisierLista(id);
      toast.success(id === 'initial' ? 'Se servește din nou fișierul inițial.' : 'Fișierul ales este acum cel activ.');
      await Promise.all([incarcaLista(), incarcaFisiere()]);
    } catch (err) {
      console.error('Activare fișier listă investiții:', err);
      toast.error(mesajEroareScriere(err, 'Activarea fișierului nu a reușit.'));
    } finally {
      setSeActiveaza(null);
    }
  };

  // ------------------------------------------------------------- obiectivele

  const deschideAdaugare = () => {
    setDeEditat(null);
    setFormDeschis(true);
  };

  const deschideEditare = (o: ObiectivInvestitie) => {
    setDeEditat(o);
    setFormDeschis(true);
  };

  const inchideFormular = () => {
    setFormDeschis(false);
    setDeEditat(null);
  };

  const sterge = async () => {
    if (!deSters) return;
    setSeSterge(true);
    try {
      await stergeObiectiv(deSters.id);
      toast.success('Obiectivul a fost șters.');
      setDeSters(null);
    } catch (err) {
      console.error('Ștergere obiectiv:', err);
      toast.error(mesajEroareScriere(err, 'Ștergerea nu a reușit.'));
    } finally {
      setSeSterge(false);
      // Și după eșec: un 404 înseamnă de obicei că rândul a dispărut deja.
      incarcaLista();
    }
  };

  if (seIncarca) {
    return <LoadingState message="Se încarcă lista de investiții..." />;
  }

  const oIncarcareActiva = fisiere?.fisiere.some((f) => f.activ) ?? fisier?.sursa === 'incarcat';

  return (
    <div className="space-y-6">
      {/* Antet */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-primary-600 text-white">
            <ListChecks className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lista de investiții</h1>
            <p className="text-gray-600 mt-1">
              Valorile afișate în{' '}
              <Link to="/lista-investitii" className="text-primary-600 hover:underline">
                pagina Lista de investiții
              </Link>{' '}
              și fișierul servit la „Descarcă Excel”
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={incarcaTot}>
            Reîncarcă
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={deschideAdaugare}
            disabled={!versiune}
            title={versiune ? undefined : 'Nu există o revizie activă a listei'}
          >
            Adaugă obiectiv
          </Button>
        </div>
      </div>

      {eroare && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">{eroare}</div>
      )}

      {/* 1. Fișierul Excel */}
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Fișierul Excel
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              Fișierul descărcat de utilizatori prin butonul „Descarcă Excel”. Încărcarea unui
              fișier nou înlocuiește <b>doar fișierul</b>; cifrele din tabelul de mai jos nu se
              modifică — ele se schimbă numai prin editare.
            </p>
            {fisier ? (
              <div className="mt-3 text-sm text-gray-700 space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 break-all">{numeDescarcare(fisier)}</span>
                  <span className="text-gray-500">({marimeFisier(fisier.marime)})</span>
                  {fisier.sursa === 'incarcat' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      încărcat
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      inițial
                    </span>
                  )}
                  {!fisier.disponibil && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-danger-50 text-danger-700">
                      lipsește de pe server
                    </span>
                  )}
                </div>
                {fisier.sursa === 'incarcat' && (fisier.incarcatDe || dataOra(fisier.incarcatLa)) && (
                  <div className="text-gray-500">
                    Încărcat
                    {fisier.incarcatDe && <> de <b>{fisier.incarcatDe}</b></>}
                    {dataOra(fisier.incarcatLa) && <> la {dataOra(fisier.incarcatLa)}</>}
                  </div>
                )}
                {fisier.sursa === 'initial' && dataLunga(fisier.verificatLa) && fisier.verificatPentruRevizia && (
                  <div className="text-gray-500">
                    Registrul inițial, verificat față de revizia {fisier.verificatPentruRevizia} la{' '}
                    {dataLunga(fisier.verificatLa)}.
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500">Nu există informații despre fișier.</p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="secondary"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={descarca}
              isLoading={seDescarca}
              disabled={!fisier?.disponibil}
            >
              Descarcă
            </Button>
            <Button
              variant="primary"
              leftIcon={<Upload className="w-4 h-4" />}
              onClick={() => inputFisier.current?.click()}
            >
              Încarcă fișier nou
            </Button>
            <input
              ref={inputFisier}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={alegeFisier}
            />
          </div>
        </div>

        {/* Istoricul încărcărilor */}
        <div className="mt-5 border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Istoricul încărcărilor</h3>
            {oIncarcareActiva && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RotateCcw className="w-4 h-4" />}
                onClick={() => activeaza('initial')}
                isLoading={seActiveaza === 'initial'}
                disabled={seActiveaza !== null || fisiere?.initial.disponibil === false}
              >
                Revino la fișierul inițial
              </Button>
            )}
          </div>
          {eroareFisiere && <p className="text-sm text-danger-600">{eroareFisiere}</p>}
          {fisiere && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fișier</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Mărime</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Încărcat</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Stare</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {fisiere.fisiere.map((f) => (
                    <tr key={f.id} className={f.activ ? 'bg-green-50/50' : ''}>
                      <td className="px-3 py-2 text-gray-900 break-all">
                        {f.numeOriginal}
                        {f.sha256 && (
                          <span className="ml-2 font-mono text-xs text-gray-400" title={`SHA-256: ${f.sha256}`}>
                            {f.sha256.slice(0, 12)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{marimeFisier(f.marime)}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {dataOra(f.incarcatLa) ?? '—'}
                        {f.incarcatDe && <span className="text-gray-400"> · {f.incarcatDe}</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <StareFisier
                          activ={f.activ}
                          disponibil={f.disponibil}
                          seActiveaza={seActiveaza === f.id}
                          blocat={seActiveaza !== null}
                          onActiveaza={() => activeaza(f.id)}
                        />
                      </td>
                    </tr>
                  ))}
                  {/* Registrul inițial stă mereu ultimul: e cel mai vechi și nu se șterge niciodată. */}
                  <tr className={fisiere.initial.activ ? 'bg-green-50/50' : ''}>
                    <td className="px-3 py-2 text-gray-900 break-all">
                      {fisiere.initial.nume || fisier?.nume || 'Fișierul inițial'}
                      <span className="ml-2 text-xs text-gray-400">fișierul inițial</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{marimeFisier(fisiere.initial.marime)}</td>
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">—</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <StareFisier
                        activ={fisiere.initial.activ}
                        disponibil={fisiere.initial.disponibil}
                        seActiveaza={seActiveaza === 'initial'}
                        blocat={seActiveaza !== null}
                        onActiveaza={() => activeaza('initial')}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              {fisiere.fisiere.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">Nu a fost încărcat încă niciun fișier.</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* 2. Obiectivele */}
      {!versiune && !eroare && (
        <Card>
          <p className="text-gray-600">
            Lista de investiții nu a fost încă importată: nu există o revizie activă de editat.
          </p>
        </Card>
      )}

      {versiune && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Obiectivele — revizia {versiune.cod}, anul {an}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Acum: {versiune.nrObiectiveCurent} obiective, alocat {miiLei(versiune.totalCurent)} mii lei.
                {' '}La import: {versiune.nrObiective} obiective, {miiLei(versiune.totalAn)} mii lei.
                {dataOra(versiune.modificataLa) && (
                  <>
                    {' '}Ultima modificare: {dataOra(versiune.modificataLa)}
                    {versiune.modificataDe && <> de {versiune.modificataDe}</>}.
                  </>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[16rem] max-w-lg">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="search"
                  value={cauta}
                  onChange={(e) => setCauta(e.target.value)}
                  placeholder="Caută după denumire, fișă, beneficiar sau proiect…"
                  aria-label="Caută în lista de investiții"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <select
                value={capitol}
                onChange={(e) => setCapitol(e.target.value)}
                aria-label="Capitol bugetar"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Toate capitolele</option>
                {capitoleDisponibile.map((c) => (
                  <option key={c} value={c}>
                    {c} {numeCapitol(c)}
                  </option>
                ))}
              </select>
              <select
                value={sectiune}
                onChange={(e) => setSectiune(e.target.value as '' | SectiuneInvestitie)}
                aria-label="Secțiune"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Toate secțiunile</option>
                {(Object.entries(date?.sectiuni ?? {}) as [string, string][]).map(([cod, den]) => (
                  <option key={cod} value={cod}>
                    {cod}. {den}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500" role="status">
              Afișate: {filtrate.length} {filtrate.length === 1 ? 'obiectiv' : 'obiective'} · alocat {an}:{' '}
              {miiLei(totalFiltrat)} mii lei
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Fișa</th>
                  <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700">Obiectiv de investiții</th>
                  <th scope="col" className="text-center py-3 px-2 font-semibold text-gray-700">Secț.</th>
                  <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Valoare totală<br />mii lei</th>
                  <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Rest la 01.01.{an}<br />mii lei</th>
                  <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Alocat {an}<br />mii lei</th>
                  <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700">Surse {an}</th>
                  <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700">Proiect</th>
                  <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700">Acțiuni</th>
                </tr>
              </thead>
              {grupuri.map(([cod, lista]) => {
                const sumaCapitol = lista.reduce((t, o) => t + o.alocatAn, 0);
                return (
                  <tbody key={cod} className="divide-y divide-gray-100">
                    <tr className="bg-primary-50/60">
                      <th colSpan={9} scope="colgroup" className="text-left py-2.5 px-3 font-semibold text-primary-800">
                        Cap. {cod} · {numeCapitol(cod) || 'capitol nou'}
                        <span className="ml-3 font-normal text-gray-600">
                          {lista.length} {lista.length === 1 ? 'obiectiv' : 'obiective'} · alocat{' '}
                          {miiLei(sumaCapitol)} mii lei
                        </span>
                      </th>
                    </tr>
                    {lista.map((o) => (
                      <RandAdmin
                        key={o.id}
                        o={o}
                        numeSursa={(s) => date?.surse?.[s] ?? s}
                        onEditeaza={() => deschideEditare(o)}
                        onSterge={() => setDeSters(o)}
                      />
                    ))}
                  </tbody>
                );
              })}
            </table>
          </div>
          {filtrate.length === 0 && (
            <p className="py-8 text-center text-gray-500" role="status">
              {obiective.length === 0 ? 'Revizia activă nu are niciun obiectiv.' : 'Niciun obiectiv nu se potrivește filtrelor.'}
            </p>
          )}
        </Card>
      )}

      {formDeschis && versiune && date && (
        <FormularObiectiv
          obiectiv={deEditat}
          an={an}
          capitole={date.capitole}
          sectiuni={date.sectiuni}
          surse={date.surse}
          proiecte={proiecte}
          onClose={inchideFormular}
          onSalvat={() => {
            inchideFormular();
            incarcaLista();
          }}
          onReincarca={incarcaLista}
        />
      )}

      {/* Confirmarea încărcării: aici se spune, a doua oară, că cifrele nu se schimbă. */}
      <Modal
        isOpen={!!fisierAles}
        onClose={() => !seIncarcaFisier && setFisierAles(null)}
        title="Încărcare fișier Excel"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setFisierAles(null)} disabled={seIncarcaFisier}>
              Anulează
            </Button>
            <Button
              variant="primary"
              onClick={confirmaIncarcarea}
              isLoading={seIncarcaFisier}
              leftIcon={<Upload className="w-4 h-4" />}
            >
              Încarcă
            </Button>
          </div>
        }
      >
        <p className="text-gray-700">
          Fișierul <strong className="break-all">„{fisierAles?.name}”</strong> ({marimeFisier(fisierAles?.size)})
          va fi cel descărcat de toți utilizatorii la „Descarcă Excel”.
        </p>
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Se înlocuiește <b>doar fișierul</b>. Cifrele din tabelul listei de investiții <b>nu</b> se
          citesc din el și rămân cele de acum; dacă fișierul are alte valori, modificați-le separat,
          prin editarea obiectivelor.
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Fișierul anterior rămâne în istoric și poate fi reactivat oricând.
        </p>
      </Modal>

      {/* Confirmarea ștergerii — cu denumirea întreagă, fișele nu sunt unice. */}
      <Modal
        isOpen={!!deSters}
        onClose={() => !seSterge && setDeSters(null)}
        title="Confirmare ștergere"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeSters(null)} disabled={seSterge}>
              Anulează
            </Button>
            <Button variant="danger" onClick={sterge} isLoading={seSterge} leftIcon={<Trash2 className="w-4 h-4" />}>
              Șterge
            </Button>
          </div>
        }
      >
        <p className="text-gray-600">Sigur doriți să ștergeți obiectivul</p>
        <p className="mt-2 font-semibold text-gray-900">„{deSters?.denumire}”</p>
        <p className="text-sm text-gray-500 mt-1">
          Fișa {deSters?.nrFisa} · Cap. {deSters?.capitol} · secțiunea {deSters?.sectiune} · alocat{' '}
          {miiLei(deSters?.alocatAn)} mii lei
        </p>
        <p className="text-sm text-red-600 mt-3">
          Obiectivul și sursele lui de finanțare dispar din lista de investiții. Ștergerea rămâne
          consemnată în jurnalul de modificări.
        </p>
      </Modal>
    </div>
  );
}

function StareFisier({
  activ,
  disponibil,
  seActiveaza,
  blocat,
  onActiveaza,
}: {
  activ: boolean;
  disponibil: boolean;
  seActiveaza: boolean;
  blocat: boolean;
  onActiveaza: () => void;
}) {
  if (activ) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="w-3 h-3" /> activ
      </span>
    );
  }
  if (!disponibil) {
    return <span className="text-xs text-danger-600">lipsește de pe server</span>;
  }
  return (
    <Button variant="ghost" size="sm" onClick={onActiveaza} isLoading={seActiveaza} disabled={blocat}>
      Activează
    </Button>
  );
}

function RandAdmin({
  o,
  numeSursa,
  onEditeaza,
  onSterge,
}: {
  o: ObiectivInvestitie;
  numeSursa: (s: SursaFinantare) => string;
  onEditeaza: () => void;
  onSterge: () => void;
}) {
  const surse = Object.entries(o.surse) as [SursaFinantare, number][];
  const meta = [o.beneficiar, o.subgrup].filter(Boolean) as string[];
  const modificat = dataOra(o.modificatLa);

  return (
    <tr className="hover:bg-gray-50 align-top">
      <td className="py-2.5 px-3 font-mono text-xs text-gray-500 whitespace-nowrap">{o.nrFisa}</td>
      <td className="py-2.5 px-3">
        <div className="text-gray-900 max-w-xl">{o.denumire}</div>
        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
          {meta.length > 0 && <span>{meta.join(' · ')}</span>}
          {esteCheltuialaVerde(o) && (
            <span className="inline-flex items-center gap-1 text-green-600">
              <Leaf className="w-3 h-3" /> cheltuială verde
            </span>
          )}
          {o.adaugatManual && (
            <span className="inline-flex items-center px-1.5 rounded bg-amber-100 text-amber-800">adăugat manual</span>
          )}
          {modificat && (
            <span className="text-gray-500">
              modificat la {modificat}
              {o.modificatDe && <> de {o.modificatDe}</>}
            </span>
          )}
        </div>
      </td>
      <td className="py-2.5 px-2 text-center font-semibold text-gray-500">{o.sectiune}</td>
      <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap text-gray-600">{miiLei(o.valoareTotala)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap text-gray-600">{miiLei(o.restExecutat)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap font-semibold text-gray-900">{miiLei(o.alocatAn)}</td>
      <td className="py-2.5 px-3 text-xs text-gray-600 whitespace-nowrap">
        {surse.length === 0
          ? '—'
          : surse.map(([cheie, suma]) => (
              <div key={cheie}>
                {numeSursa(cheie)} {miiLei(suma)}
              </div>
            ))}
        {surseleNuSeInchid(o) && (
          <div className="mt-0.5 flex items-start gap-1 text-danger-600">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Sursele însumează {miiLei(sumaSurselor(o))}.</span>
          </div>
        )}
      </td>
      <td className="py-2.5 px-3 text-xs">
        {o.legatura === 'vizibil' && o.proiectId !== null ? (
          <Link
            to={`/projects/${o.proiectId}`}
            className="inline-flex items-start gap-1 text-primary-600 hover:text-primary-700 hover:underline"
          >
            <span>{o.proiectNume}</span>
            <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
          </Link>
        ) : o.legatura === 'ascuns' ? (
          <span className="text-gray-400">proiect inactiv</span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="py-2.5 px-3 text-right whitespace-nowrap">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" leftIcon={<Edit2 className="w-4 h-4" />} onClick={onEditeaza}>
            Editează
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 className="w-4 h-4" />}
            onClick={onSterge}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Șterge
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// Formularul unui obiectiv (adăugare / editare).
//
// La editare se trimit DOAR câmpurile schimbate față de rândul deschis: o
// legătură spre un proiect devenit inactiv sosește ca `proiectId: null`, iar un
// PATCH cu toate câmpurile ar dezlega-o fără ca omul să fi atins-o. Diferența
// dă și un jurnal de audit care arată ce a schimbat omul, nu tot rândul.
// ============================================================================

interface StareFormular {
  nrFisa: string;
  capitol: string;
  capitolManual: boolean; // cod nou, în afara nomenclatorului
  sectiune: '' | SectiuneInvestitie;
  denumire: string;
  beneficiar: string;
  subgrup: string;
  eticheta: string;
  valoareTotala: string;
  restExecutat: string;
  alocatAn: string;
  surse: Record<SursaFinantare, string>;
  proiectId: string; // '' = fără proiect
}

function stareDin(o: ObiectivInvestitie | null, capitole: Record<string, string>): StareFormular {
  const surse = {} as Record<SursaFinantare, string>;
  CODURI_SURSE.forEach((s) => {
    surse[s] = miiLeiEditabil(o?.surse[s]);
  });
  return {
    nrFisa: o?.nrFisa ?? '',
    capitol: o?.capitol ?? '',
    capitolManual: !!o && !(o.capitol in capitole),
    sectiune: o?.sectiune ?? '',
    denumire: o?.denumire ?? '',
    beneficiar: o?.beneficiar ?? '',
    subgrup: o?.subgrup ?? '',
    // Un rând nou pornește FĂRĂ etichetă: „verde" e o afirmație despre bani
    // (apare cu frunză în pagina publică), nu o valoare implicită comodă.
    eticheta: o?.eticheta ?? '',
    valoareTotala: miiLeiEditabil(o?.valoareTotala),
    restExecutat: miiLeiEditabil(o?.restExecutat),
    alocatAn: o ? miiLeiEditabil(o.alocatAn) : '0,00',
    surse,
    proiectId: o?.proiectId !== null && o?.proiectId !== undefined ? String(o.proiectId) : '',
  };
}

const textSauNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

function FormularObiectiv({
  obiectiv,
  an,
  capitole,
  sectiuni,
  surse: numeSurse,
  proiecte,
  onClose,
  onSalvat,
  onReincarca,
}: {
  obiectiv: ObiectivInvestitie | null;
  an: number;
  capitole: Record<string, string>;
  sectiuni: Record<string, string>;
  surse: Record<string, string>;
  proiecte: { id: number; nume: string }[];
  onClose: () => void;
  onSalvat: () => void;
  onReincarca: () => Promise<RaspunsListaInvestitii | null>;
}) {
  // Rândul față de care se calculează diferența și al cărui `modificatLa` se
  // trimite. Se înlocuiește după un conflict (409) cu versiunea din bază.
  const [baza, setBaza] = useState<ObiectivInvestitie | null>(obiectiv);
  const [f, setF] = useState<StareFormular>(() => stareDin(obiectiv, capitole));
  const [erori, setErori] = useState<Record<string, string>>({});
  const [eroareServer, setEroareServer] = useState<string | null>(null);
  const [seSalveaza, setSeSalveaza] = useState(false);
  const esteEditare = baza !== null;

  const set = <K extends keyof StareFormular>(cheie: K, valoare: StareFormular[K]) =>
    setF((p) => ({ ...p, [cheie]: valoare }));

  const coduriSurse = useMemo(() => {
    const din = Object.keys(numeSurse).filter((k): k is SursaFinantare => CODURI_SURSE.includes(k as SursaFinantare));
    return din.length > 0 ? din : CODURI_SURSE;
  }, [numeSurse]);

  const optiuniCapitol = useMemo(() => {
    const o = Object.entries(capitole).map(([cod, den]) => ({ value: cod, label: `${cod} ${den}` }));
    o.push({ value: CAPITOL_NOU, label: 'Alt cod (în afara nomenclatorului)…' });
    return o;
  }, [capitole]);

  const optiuniEticheta = useMemo(() => {
    const o = [
      { value: ETICHETA_VERDE, label: 'Cheltuială verde/favorabilă' },
      { value: ETICHETA_NEUTRA, label: 'Cheltuială neutră' },
      { value: '', label: 'Fără etichetă' },
    ];
    // O grafie existentă, alta decât cele două, se păstrează ca opțiune: altfel
    // simpla deschidere a formularului n-ar mai putea-o reprezenta.
    const actuala = baza?.eticheta;
    if (actuala && actuala !== ETICHETA_VERDE && actuala !== ETICHETA_NEUTRA) {
      o.unshift({ value: actuala, label: actuala });
    }
    return o;
  }, [baza]);

  const optiuniProiect = useMemo(
    () => [{ value: '', label: 'Fără proiect' }, ...proiecte.map((p) => ({ value: String(p.id), label: p.nume }))],
    [proiecte]
  );

  // Sumele citite la fiecare tastă — pentru erori pe loc și pentru avertismentul
  // despre surse. Un câmp cu eroare contează ca 0 la însumare.
  const sume = useMemo(() => {
    const alocat = parseazaMiiLei(f.alocatAn);
    const surseCitite = coduriSurse.map((s) => [s, parseazaMiiLei(f.surse[s])] as const);
    const totalSurse = surseCitite.reduce((t, [, r]) => t + (r.lei ?? 0), 0);
    const areSurse = surseCitite.some(([, r]) => r.lei !== null && r.lei !== undefined && r.lei !== 0);
    return { alocat, surseCitite, totalSurse, areSurse };
  }, [f.alocatAn, f.surse, coduriSurse]);

  const alocatLei = sume.alocat.lei ?? 0;
  const surseDiferite = sume.areSurse && Math.abs(sume.totalSurse - alocatLei) > 0.5;

  const valideaza = (): { date: DateObiectivInvestitie; erori: Record<string, string> } => {
    const e: Record<string, string> = {};
    const nrFisa = f.nrFisa.trim();
    const denumire = f.denumire.trim();
    const capitol = f.capitol.trim();
    if (!nrFisa) e.nrFisa = 'Fișa este obligatorie.';
    else if (nrFisa.length > 40) e.nrFisa = 'Cel mult 40 de caractere.';
    if (!/^\d{2}\.\d{2}$/.test(capitol)) e.capitol = 'Codul de capitol are forma 00.00 (ex. 84.02).';
    if (!f.sectiune) e.sectiune = 'Alegeți secțiunea.';
    if (!denumire) e.denumire = 'Denumirea este obligatorie.';
    else if (denumire.length > 1000) e.denumire = 'Cel mult 1000 de caractere.';
    (['beneficiar', 'subgrup'] as const).forEach((k) => {
      if (f[k].trim().length > 300) e[k] = 'Cel mult 300 de caractere.';
    });

    const valoareTotala = parseazaMiiLei(f.valoareTotala);
    const restExecutat = parseazaMiiLei(f.restExecutat);
    if (valoareTotala.eroare) e.valoareTotala = valoareTotala.eroare;
    if (restExecutat.eroare) e.restExecutat = restExecutat.eroare;
    if (sume.alocat.eroare) e.alocatAn = sume.alocat.eroare;
    else if (sume.alocat.lei === null) e.alocatAn = 'Suma alocată este obligatorie (0 dacă nu are).';
    const surse: Partial<Record<SursaFinantare, number | null>> = {};
    sume.surseCitite.forEach(([s, r]) => {
      if (r.eroare) e[`sursa_${s}`] = r.eroare;
      // 0 și gol înseamnă același lucru pentru backend: sursa lipsește.
      else surse[s] = r.lei === 0 ? null : r.lei ?? null;
    });

    const date: DateObiectivInvestitie = {
      nrFisa,
      capitol,
      sectiune: f.sectiune || undefined,
      denumire,
      beneficiar: textSauNull(f.beneficiar),
      subgrup: textSauNull(f.subgrup),
      eticheta: textSauNull(f.eticheta),
      valoareTotala: valoareTotala.lei ?? null,
      restExecutat: restExecutat.lei ?? null,
      alocatAn: sume.alocat.lei ?? 0,
      surse,
      proiectId: f.proiectId ? Number(f.proiectId) : null,
    };
    return { date, erori: e };
  };

  // Doar ce diferă de rândul deschis. `null` = nimic de trimis.
  const diferente = (d: DateObiectivInvestitie, o: ObiectivInvestitie): DateObiectivInvestitie | null => {
    const r: DateObiectivInvestitie = {};
    if (d.nrFisa !== o.nrFisa) r.nrFisa = d.nrFisa;
    if (d.capitol !== o.capitol) r.capitol = d.capitol;
    if (d.sectiune !== o.sectiune) r.sectiune = d.sectiune;
    if (d.denumire !== o.denumire) r.denumire = d.denumire;
    if (d.beneficiar !== (o.beneficiar ?? null)) r.beneficiar = d.beneficiar;
    if (d.subgrup !== (o.subgrup ?? null)) r.subgrup = d.subgrup;
    if (d.eticheta !== (o.eticheta ?? null)) r.eticheta = d.eticheta;
    if (d.valoareTotala !== o.valoareTotala) r.valoareTotala = d.valoareTotala;
    if (d.restExecutat !== o.restExecutat) r.restExecutat = d.restExecutat;
    if (d.alocatAn !== o.alocatAn) r.alocatAn = d.alocatAn;
    const surseSchimbate = CODURI_SURSE.some((s) => (d.surse?.[s] ?? null) !== (o.surse[s] ?? null));
    // `surse` înlocuiește complet alocările, deci pleacă întreg, nu doar sursa atinsă.
    if (surseSchimbate) r.surse = d.surse;
    if (d.proiectId !== o.proiectId) r.proiectId = d.proiectId;
    return Object.keys(r).length > 0 ? r : null;
  };

  const salveaza = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    // Enter pe butonul de trimitere ascuns ocoleste butonul dezactivat: fara garda, a doua
    // cerere ar pleca in timp ce prima e in curs (la adaugare = obiectiv dublat).
    if (seSalveaza) return;
    setEroareServer(null);
    const { date, erori: e } = valideaza();
    setErori(e);
    if (Object.keys(e).length > 0) return;

    setSeSalveaza(true);
    try {
      if (baza) {
        const dif = diferente(date, baza);
        if (!dif) {
          toast('Nicio modificare de salvat.');
          onClose();
          return;
        }
        await actualizeazaObiectiv(baza.id, { ...dif, asteptatModificatLa: baza.modificatLa });
        toast.success('Obiectivul a fost actualizat.');
      } else {
        await adaugaObiectiv(date);
        toast.success('Obiectivul a fost adăugat.');
      }
      onSalvat();
    } catch (err) {
      console.error('Salvare obiectiv:', err);
      const conflict = conflictObiectiv(err);
      if (conflict && baza) {
        // Altcineva a salvat între timp. Formularul trece pe valorile din bază:
        // a le păstra pe ale noastre și a re-trimite ar călca exact peste
        // modificarea pe care 409-ul tocmai a semnalat-o.
        const lista = await onReincarca();
        const proaspat = conflict.obiectiv ?? lista?.obiective.find((x) => x.id === baza.id) ?? null;
        if (proaspat) {
          setBaza(proaspat);
          setF(stareDin(proaspat, capitole));
          setErori({});
        }
        setEroareServer(
          `${conflict.mesaj} Formularul arată acum valorile salvate; reaplicați modificările dacă mai e cazul.`
        );
      } else {
        setEroareServer(mesajEroareScriere(err, 'Salvarea nu a reușit.'));
        // 404 = rândul nu mai e în revizia activă (șters sau revizie schimbată).
        const status = (err as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) onReincarca();
      }
    } finally {
      setSeSalveaza(false);
    }
  };

  const hintLei = (text: string): string | undefined => {
    const r = parseazaMiiLei(text);
    if (r.eroare || r.lei === null || r.lei === undefined) return undefined;
    return `= ${r.lei.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;
  };

  const eroareSuma = (text: string): string | undefined => parseazaMiiLei(text).eroare;

  return (
    <Modal
      isOpen
      onClose={() => !seSalveaza && onClose()}
      closeOnOverlayClick={false}
      title={esteEditare ? 'Editează obiectivul' : 'Adaugă obiectiv'}
      size="full"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={seSalveaza}>
            Anulează
          </Button>
          <Button variant="primary" onClick={() => salveaza()} isLoading={seSalveaza}>
            {esteEditare ? 'Salvează' : 'Adaugă'}
          </Button>
        </div>
      }
    >
      <form onSubmit={salveaza} className="space-y-5">
        {eroareServer && (
          <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
            {eroareServer}
          </div>
        )}
        {baza && (baza.adaugatManual || dataOra(baza.modificatLa)) && (
          <p className="text-xs text-gray-500">
            {baza.adaugatManual ? 'Rând adăugat manual (nu provine din Excel).' : `Rândul ${baza.randExcel} din Excel.`}
            {dataOra(baza.modificatLa) && (
              <>
                {' '}Ultima modificare: {dataOra(baza.modificatLa)}
                {baza.modificatDe && <> de {baza.modificatDe}</>}.
              </>
            )}
          </p>
        )}

        {/* Identificare */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="Fișa"
            value={f.nrFisa}
            onChange={(e) => set('nrFisa', e.target.value)}
            error={erori.nrFisa}
            maxLength={40}
            required
          />
          <div>
            <SearchableSelect
              label="Capitol"
              required
              clearable={false}
              options={optiuniCapitol}
              value={f.capitolManual ? CAPITOL_NOU : f.capitol}
              onChange={(v) => {
                const cod = String(v);
                if (cod === CAPITOL_NOU) setF((p) => ({ ...p, capitolManual: true, capitol: '' }));
                else setF((p) => ({ ...p, capitolManual: false, capitol: cod }));
              }}
              error={f.capitolManual ? undefined : erori.capitol}
            />
            {f.capitolManual && (
              <div className="mt-2">
                <Input
                  placeholder="ex. 80.02"
                  value={f.capitol}
                  onChange={(e) => set('capitol', e.target.value)}
                  error={erori.capitol}
                  hint="Cod de clasificație bugetară, forma 00.00."
                  aria-label="Cod de capitol nou"
                />
              </div>
            )}
          </div>
          <SearchableSelect
            label="Secțiune"
            required
            clearable={false}
            options={Object.entries(sectiuni).map(([cod, den]) => ({ value: cod, label: `${cod}. ${den}` }))}
            value={f.sectiune}
            onChange={(v) => set('sectiune', String(v) as '' | SectiuneInvestitie)}
            error={erori.sectiune}
          />
        </div>

        <div>
          <label htmlFor="obiectiv-denumire" className="block text-sm font-medium text-gray-700 mb-1">
            Denumire<span className="text-danger-500 ml-1">*</span>
          </label>
          <textarea
            id="obiectiv-denumire"
            value={f.denumire}
            onChange={(e) => set('denumire', e.target.value)}
            rows={3}
            maxLength={1000}
            className={`block w-full rounded-lg border shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              erori.denumire ? 'border-danger-500' : 'border-gray-300'
            }`}
          />
          {erori.denumire && <p className="mt-1 text-sm text-danger-500">{erori.denumire}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            label="Beneficiar"
            value={f.beneficiar}
            onChange={(e) => set('beneficiar', e.target.value)}
            error={erori.beneficiar}
            maxLength={300}
          />
          <Input
            label="Subgrup"
            value={f.subgrup}
            onChange={(e) => set('subgrup', e.target.value)}
            error={erori.subgrup}
            maxLength={300}
            hint="ex. Dotări independente"
          />
          <SearchableSelect
            label="Etichetă"
            clearable={false}
            options={optiuniEticheta}
            value={f.eticheta}
            onChange={(v) => set('eticheta', String(v))}
          />
        </div>

        {/* Sume */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Sume (mii lei)</h3>
          <p className="text-xs text-gray-500 mb-2">
            Ca în documentul oficial. Se acceptă 1.234,56 sau 1234,56 sau 1234.56.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Valoare totală"
              inputMode="decimal"
              value={f.valoareTotala}
              onChange={(e) => set('valoareTotala', e.target.value)}
              error={erori.valoareTotala ?? eroareSuma(f.valoareTotala)}
              hint={hintLei(f.valoareTotala) ?? 'Gol = necompletat'}
            />
            <Input
              label={`Rest de executat la 01.01.${an}`}
              inputMode="decimal"
              value={f.restExecutat}
              onChange={(e) => set('restExecutat', e.target.value)}
              error={erori.restExecutat ?? eroareSuma(f.restExecutat)}
              hint={hintLei(f.restExecutat) ?? 'Gol = necompletat'}
            />
            <Input
              label={`Alocat ${an}`}
              inputMode="decimal"
              required
              value={f.alocatAn}
              onChange={(e) => set('alocatAn', e.target.value)}
              error={erori.alocatAn ?? eroareSuma(f.alocatAn)}
              hint={hintLei(f.alocatAn)}
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-800">Surse de finanțare {an} (mii lei)</h3>
          <p className="text-xs text-gray-500 mb-2">Gol sau 0 = sursa nu finanțează obiectivul.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {coduriSurse.map((s) => (
              <Input
                key={s}
                label={numeSurse[s] ?? s}
                inputMode="decimal"
                value={f.surse[s]}
                onChange={(e) => {
                  const v = e.target.value;
                  setF((p) => ({ ...p, surse: { ...p.surse, [s]: v } }));
                }}
                error={erori[`sursa_${s}`] ?? eroareSuma(f.surse[s])}
              />
            ))}
          </div>
          {/* Avertisment, nu blocare: chiar Excelul oficial are rânduri unde sursele
              nu închid totalul, iar pagina redă anexa, nu o corectează. */}
          {surseDiferite && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Sursele însumează {miiLei(sume.totalSurse)} mii lei, iar alocat pe {an} este{' '}
                {miiLei(alocatLei)} mii lei (diferență {miiLei(sume.totalSurse - alocatLei)}). Se poate
                salva așa, dar verificați.
              </span>
            </div>
          )}
          {!sume.areSurse && alocatLei !== 0 && (
            <p className="mt-2 text-xs text-amber-700">Nicio sursă de finanțare completată.</p>
          )}
        </div>

        <div>
          <SearchableSelect
            label="Proiect legat în platformă"
            options={optiuniProiect}
            value={f.proiectId}
            onChange={(v) => set('proiectId', String(v))}
            placeholder="Fără proiect"
            hint={
              baza?.legatura === 'ascuns'
                ? 'Obiectivul e legat de un proiect inactiv. Alegerea unui proiect înlocuiește legătura; lăsat așa, rămâne neatinsă.'
                : 'Opțional. Legătura nu se arată deocamdată în pagina publică.'
            }
          />
        </div>

        {/* Enter într-un câmp trimite formularul; butonul vizibil e în subsolul modalului. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
